import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { SessionDetail } from '../../api/sessions';
import { colours, radius } from '../../theme';

const H = 200;
const PAD = { top: 12, right: 12, bottom: 28, left: 44 };
const clock = new Intl.DateTimeFormat('en-AU', { hour: 'numeric', minute: '2-digit' });

/** A round top for the y axis: 7.4 -> 8, 22 -> 25, 0.6 -> 1. */
function niceMax(v: number): number {
  if (v <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(v));
  return ([1, 2, 2.5, 5, 10].find((m) => m * p >= v) ?? 10) * p;
}

/**
 * The session's power over time (or, for chargers that only report energy, kWh delivered),
 * one line on one axis. Hover or tap for the reading at that moment.
 */
export function SessionChart({ session }: { session: SessionDetail }) {
  const box = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [hover, setHover] = useState<number | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(240, e!.contentRect.width)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const data = useMemo(() => {
    const withPower = session.samples.filter((s) => s.powerW != null);
    const usePower = withPower.length >= 2;
    const pts = (usePower ? withPower : session.samples.filter((s) => s.energyWh != null)).map((s) => ({
      t: new Date(s.at).getTime(),
      v: usePower ? s.powerW! / 1000 : Math.max(0, (s.energyWh! - session.meterStartWh) / 1000),
      kwh: s.energyWh != null ? Math.max(0, (s.energyWh - session.meterStartWh) / 1000) : null,
      kw: s.powerW != null ? s.powerW / 1000 : null,
    }));
    return { usePower, pts };
  }, [session]);

  // Too few readings (e.g. sessions brought across from the old Pulsepoint): no chart at all.
  if (data.pts.length < 2) return null;

  const { pts, usePower } = data;
  const t0 = pts[0]!.t;
  const t1 = pts.at(-1)!.t;
  const top = niceMax(Math.max(...pts.map((p) => p.v)));
  const plotW = width - PAD.left - PAD.right;
  const plotH = H - PAD.top - PAD.bottom;
  const x = (t: number) => PAD.left + ((t - t0) / Math.max(1, t1 - t0)) * plotW;
  const y = (v: number) => PAD.top + plotH - (v / top) * plotH;
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${x(p.t).toFixed(1)},${y(p.v).toFixed(1)}`).join('');
  const yTicks = [0, top / 2, top];
  const xTicks = Array.from({ length: 4 }, (_, i) => t0 + ((t1 - t0) * i) / 3);
  const unit = usePower ? 'kW' : 'kWh';
  const h = hover != null ? pts[hover]! : null;

  const onMove = (clientX: number) => {
    const rect = box.current!.getBoundingClientRect();
    const t = t0 + ((clientX - rect.left - PAD.left) / plotW) * (t1 - t0);
    let best = 0;
    for (let i = 1; i < pts.length; i++) if (Math.abs(pts[i]!.t - t) < Math.abs(pts[best]!.t - t)) best = i;
    setHover(best);
  };

  return (
    <Box>
      <Typography sx={{ fontWeight: 600, mb: 1 }}>{usePower ? 'Power' : 'Energy delivered'}</Typography>
      <Box
        ref={box}
        sx={{ position: 'relative', touchAction: 'pan-y' }}
        onMouseMove={(e) => onMove(e.clientX)}
        onMouseLeave={() => setHover(null)}
        onTouchStart={(e) => onMove(e.touches[0]!.clientX)}
        onTouchMove={(e) => onMove(e.touches[0]!.clientX)}
      >
        <svg width={width} height={H} role="img" aria-label={`${usePower ? 'Power' : 'Energy'} during the session, up to ${Math.max(...pts.map((p) => p.v)).toFixed(1)} ${unit}`} style={{ display: 'block' }}>
          {yTicks.map((v) => (
            <g key={v}>
              <line x1={PAD.left} x2={width - PAD.right} y1={y(v)} y2={y(v)} stroke={colours.hairline} strokeWidth={1} />
              <text x={PAD.left - 8} y={y(v) + 4} textAnchor="end" fontSize={12} fill={colours.graphite}>
                {Number.isInteger(v) ? v : v.toFixed(1)}
              </text>
            </g>
          ))}
          <text x={PAD.left - 8} y={PAD.top - 2} textAnchor="end" fontSize={11} fill={colours.graphite} dy={-2}>
            {unit}
          </text>
          {xTicks.map((t, i) => (
            <text key={t} x={x(t)} y={H - 8} textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'} fontSize={12} fill={colours.graphite}>
              {clock.format(t)}
            </text>
          ))}
          <path d={path} fill="none" stroke={colours.signalTeal} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {h && (
            <g>
              <line x1={x(h.t)} x2={x(h.t)} y1={PAD.top} y2={PAD.top + plotH} stroke={colours.graphite} strokeWidth={1} strokeDasharray="3 3" />
              <circle cx={x(h.t)} cy={y(h.v)} r={5} fill={colours.signalTeal} stroke={colours.white} strokeWidth={2} />
            </g>
          )}
        </svg>
        {h && (
          <Box
            role="status"
            sx={{
              position: 'absolute',
              top: 0,
              left: Math.min(Math.max(x(h.t) + 12, 0), width - 160),
              pointerEvents: 'none',
              bgcolor: colours.white,
              border: `1px solid ${colours.hairline}`,
              borderRadius: `${radius.button}px`,
              px: 1.5,
              py: 1,
              minWidth: 140,
            }}
          >
            <Typography variant="body2" color="textSecondary">{clock.format(h.t)}</Typography>
            {h.kw != null && <Typography variant="body2" className="num" sx={{ fontWeight: 500 }}>{h.kw.toFixed(1)} kW</Typography>}
            {h.kwh != null && <Typography variant="body2" className="num">{h.kwh.toFixed(2)} kWh so far</Typography>}
          </Box>
        )}
      </Box>
    </Box>
  );
}
